/**
 * Excel parser — File → ImportRecord[].
 *
 * Pure data transform: produces records but does NOT validate them.
 * Validation belongs to `validator.ts` so the two concerns can be unit-tested
 * in isolation.
 *
 * `xlsx` is loaded via dynamic import (cached) so it does not hit the initial
 * bundle. Tests can opt out by calling `parseWorkbook(workbook, ...)` directly
 * with a workbook constructed from `aoa_to_sheet` inline.
 */

import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import type { ExcelSheetSpec, ImportRecord } from '../model/types'
import { buildAllSheetSpecs, indexSpecsBySheetName } from './columnSchema'
import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILE_SIZE_BYTES,
  MAX_ROWS_PER_SHEET,
  MAX_TOTAL_ROWS,
} from './constants'

// ─────────────────────────────────────────────────────────────────────────────
// Errors — thrown by file-level checks before the workbook is opened.
// UI catches these and renders toasts (per design §6.2).
// ─────────────────────────────────────────────────────────────────────────────

export class ExcelParseError extends Error {
  readonly code:
    | 'UNSUPPORTED_FORMAT'
    | 'FILE_TOO_LARGE'
    | 'CORRUPTED'
    | 'ENCRYPTED'
    | 'TOO_MANY_ROWS'
  constructor(
    code: ExcelParseError['code'],
    message: string
  ) {
    super(message)
    this.name = 'ExcelParseError'
    this.code = code
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// xlsx dynamic loader (shared module-level cache between exporter & parser)
// ─────────────────────────────────────────────────────────────────────────────

type XLSXModule = typeof import('xlsx')

let xlsxPromise: Promise<XLSXModule> | null = null

async function loadXlsx(): Promise<XLSXModule> {
  if (!xlsxPromise) {
    xlsxPromise = import('xlsx')
  }
  return xlsxPromise
}

// ─────────────────────────────────────────────────────────────────────────────
// File checks
// ─────────────────────────────────────────────────────────────────────────────

function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot < 0 ? '' : filename.slice(dot).toLowerCase()
}

function assertSupportedFile(file: File): void {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new ExcelParseError(
      'FILE_TOO_LARGE',
      `파일 크기(${(file.size / 1024 / 1024).toFixed(1)}MB)가 한도(${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB)를 초과했습니다.`
    )
  }
  const ext = getFileExtension(file.name)
  if (!ACCEPTED_FILE_EXTENSIONS.includes(ext)) {
    throw new ExcelParseError(
      'UNSUPPORTED_FORMAT',
      `지원되지 않는 파일 형식입니다 (${ext || '확장자 없음'}). .xlsx 또는 .xls 만 지원됩니다.`
    )
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Workbook → records
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert one xlsx sheet to ImportRecord[]. Empty cells become `undefined`
 * (NOT empty string) so the validator can distinguish "user blanked the cell"
 * from "user wrote an empty string".
 *
 * Headers are read from row 1 of the sheet (not from the spec) so that an
 * out-of-order or partial Excel still parses; missing columns simply yield
 * `undefined` per record. Extra columns are kept on the record's `fields`
 * map and surfaced as warnings later.
 */
export function parseSheetToRecords(
  XLSX: XLSXModule,
  worksheet: import('xlsx').WorkSheet,
  spec: ExcelSheetSpec
): ImportRecord[] {
  // header: 1 → array-of-arrays. defval: undefined → empty cells stay undefined.
  // raw: false → numbers/booleans coerced to formatted strings only when needed;
  //              we still receive proper number/boolean types for typed cells.
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    defval: undefined,
    blankrows: false,
  })

  if (aoa.length === 0) return []

  const headerRow = (aoa[0] ?? []).map(v => (typeof v === 'string' ? v.trim() : ''))
  const dataRows = aoa.slice(1)

  if (dataRows.length > MAX_ROWS_PER_SHEET) {
    throw new ExcelParseError(
      'TOO_MANY_ROWS',
      `시트 "${spec.sheetName}" 행 수(${dataRows.length})가 한도(${MAX_ROWS_PER_SHEET})를 초과했습니다.`
    )
  }

  const records: ImportRecord[] = []

  dataRows.forEach((row, idx) => {
    const fields: Record<string, unknown> = {}
    headerRow.forEach((header, colIdx) => {
      if (!header) return
      const v = row[colIdx]
      // Treat empty strings as undefined to mean "no change" (design §3.2).
      fields[header] = v === '' ? undefined : v
    })

    // id is the first column by convention; fall back to fields.id for safety
    // when the user reorders columns.
    const idValue = fields.id !== undefined ? fields.id : row[0]
    const id = idValue == null ? '' : String(idValue).trim()

    // Skip rows that are entirely empty (no header value and no id).
    const hasAnyValue =
      Object.values(fields).some(v => v !== undefined && v !== '') ||
      id.length > 0
    if (!hasAnyValue) return

    records.push({
      rowNumber: idx + 2, // header is row 1; first data row is row 2
      sheetName: spec.sheetName,
      typeId: spec.typeId,
      id,
      fields,
    })
  })

  return records
}

/**
 * Parse a fully-loaded workbook to records. Sheets without a matching spec
 * are skipped (logged in caller's responsibility).
 */
export function parseWorkbookToRecords(
  XLSX: XLSXModule,
  workbook: import('xlsx').WorkBook,
  specs: ExcelSheetSpec[]
): { records: ImportRecord[]; skippedSheets: string[] } {
  const specsByName = indexSpecsBySheetName(specs)
  const records: ImportRecord[] = []
  const skippedSheets: string[] = []

  for (const sheetName of workbook.SheetNames) {
    const spec = specsByName.get(sheetName)
    if (!spec) {
      skippedSheets.push(sheetName)
      continue
    }
    const ws = workbook.Sheets[sheetName]
    if (!ws) continue
    const sheetRecords = parseSheetToRecords(XLSX, ws, spec)
    records.push(...sheetRecords)

    if (records.length > MAX_TOTAL_ROWS) {
      throw new ExcelParseError(
        'TOO_MANY_ROWS',
        `전체 행 수가 한도(${MAX_TOTAL_ROWS})를 초과했습니다.`
      )
    }
  }

  return { records, skippedSheets }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public entry — File-based parsing
// ─────────────────────────────────────────────────────────────────────────────

export interface ParseResult {
  records: ImportRecord[]
  /** Sheet names found in the workbook but absent from the active object types. */
  skippedSheets: string[]
}

/**
 * Read an uploaded File and return parsed records.
 * Throws `ExcelParseError` on file-level failures. Schema/value validation
 * happens later in `validator.ts`.
 */
export async function parseExcelFile(
  file: File,
  objectTypes: ObjectType[]
): Promise<ParseResult> {
  assertSupportedFile(file)

  const XLSX = await loadXlsx()
  let workbook: import('xlsx').WorkBook
  try {
    const buffer = await file.arrayBuffer()
    workbook = XLSX.read(buffer, { type: 'array', cellDates: false })
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    if (/password|encrypt/i.test(msg)) {
      throw new ExcelParseError('ENCRYPTED', '암호화된 파일은 지원하지 않습니다.')
    }
    throw new ExcelParseError('CORRUPTED', `Excel 파일을 읽을 수 없습니다: ${msg}`)
  }

  const specs = buildAllSheetSpecs(objectTypes)
  return parseWorkbookToRecords(XLSX, workbook, specs)
}
